import type { z } from 'zod';
import type { DeviceProof } from '../fleetRecoveryTypes';
import { FLEET_RECOVERY_ENVELOPE_DOMAIN } from './scalars';

/** Canonical wire bytes shared by release signing, request binding and native golden vectors. */
export class FleetRecoveryCanonicalJson {
  public static stringify(value: unknown): string {
    return this.serialize(value, 0, new Set());
  }

  private static serialize(value: unknown, depth: number, ancestors: Set<object>): string {
    if (depth > 64) throw new Error('fleet_recovery_json_depth');
    if (typeof value === 'string' && /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/.test(value)) throw new Error('fleet_recovery_json_unicode_invalid');
    if (value === null || typeof value === 'boolean' || typeof value === 'string') return JSON.stringify(value);
    if (typeof value === 'number') {
      if (!Number.isSafeInteger(value) || Object.is(value, -0)) throw new Error('fleet_recovery_integer_invalid');
      return String(value);
    }
    if (typeof value !== 'object') throw new Error('fleet_recovery_json_invalid');
    const prototype = Object.getPrototypeOf(value);
    if (!Array.isArray(value) && prototype !== null && (Object.getPrototypeOf(prototype) !== null
      || prototype.constructor?.name !== 'Object')) throw new Error('fleet_recovery_json_invalid');
    if (ancestors.has(value)) throw new Error('fleet_recovery_json_cycle');
    ancestors.add(value);
    try {
      if (Array.isArray(value)) return `[${Array.from(value, item => this.serialize(item, depth + 1, ancestors)).join(',')}]`;
      return `{${Object.keys(value).sort().map(key => `${this.serialize(key, depth + 1, ancestors)}:${this.serialize((value as Record<string, unknown>)[key], depth + 1, ancestors)}`).join(',')}}`;
    } finally { ancestors.delete(value); }
  }

  public static envelopePayload(payload: unknown): string {
    return FLEET_RECOVERY_ENVELOPE_DOMAIN + this.stringify(payload);
  }

  public static requestPayload(method: string, pathname: string, body: Record<string, unknown>): string {
    if (!/^(POST|GET)$/.test(method) || !/^\/api\/recovery\/v2\/[A-Za-z0-9_/-]+$/.test(pathname)
      || pathname.includes('//') || pathname.endsWith('/')) throw new Error('fleet_recovery_request_path_invalid');
    const { proof: _proof, ...unsignedBody } = body;
    return this.stringify({ method, pathname, body: unsignedBody });
  }

  public static deviceProofPayload(proof: Omit<DeviceProof, 'signature'>): string {
    return this.stringify(proof);
  }
}

/** Reject duplicate keys/deep inputs before JSON.parse can discard signed ambiguity. */
export class FleetRecoveryWireDecoder {
  private offset = 0;
  private constructor(private readonly json: string) {}

  public static parse<T>(json: string, schema: z.ZodType<T>, maxBytes = 8 * 1024 * 1024): T {
    if (new TextEncoder().encode(json).length > maxBytes) throw new Error('fleet_recovery_payload_too_large');
    const decoder = new FleetRecoveryWireDecoder(json);
    decoder.value(0);
    decoder.whitespace();
    if (decoder.offset !== json.length) throw new Error('fleet_recovery_json_invalid');
    return schema.parse(JSON.parse(json));
  }

  private whitespace(): void {
    while (/^[\x20\x09\x0a\x0d]$/.test(this.json[this.offset] ?? '')) this.offset++;
  }

  private value(depth: number): void {
    if (depth > 64) throw new Error('fleet_recovery_json_depth');
    this.whitespace();
    const token = this.json[this.offset];
    if (token === '{') { this.object(depth + 1); return; }
    if (token === '[') { this.array(depth + 1); return; }
    if (token === '"') { this.string(); return; }
    const scalar = /^(?:true|false|null|-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?)/.exec(this.json.slice(this.offset));
    if (!scalar) throw new Error('fleet_recovery_json_invalid');
    this.offset += scalar[0].length;
  }

  private string(): string {
    const start = this.offset++;
    let escaped = false;
    while (this.offset < this.json.length) {
      const character = this.json[this.offset++];
      if (character === '"' && !escaped) {
        const parsed = JSON.parse(this.json.slice(start, this.offset));
        FleetRecoveryCanonicalJson.stringify(parsed);
        return parsed;
      }
      escaped = character === '\\' && !escaped;
    }
    throw new Error('fleet_recovery_json_invalid');
  }

  private object(depth: number): void {
    const keys = new Set<string>();
    this.offset++;
    this.whitespace();
    if (this.json[this.offset] === '}') { this.offset++; return; }
    while (true) {
      this.whitespace();
      if (this.json[this.offset] !== '"') throw new Error('fleet_recovery_json_invalid');
      const key = this.string();
      if (keys.has(key)) throw new Error('fleet_recovery_duplicate_key');
      keys.add(key);
      this.whitespace();
      if (this.json[this.offset++] !== ':') throw new Error('fleet_recovery_json_invalid');
      this.value(depth);
      this.whitespace();
      const separator = this.json[this.offset++];
      if (separator === '}') return;
      if (separator !== ',') throw new Error('fleet_recovery_json_invalid');
    }
  }

  private array(depth: number): void {
    this.offset++;
    this.whitespace();
    if (this.json[this.offset] === ']') { this.offset++; return; }
    while (true) {
      this.value(depth);
      this.whitespace();
      const separator = this.json[this.offset++];
      if (separator === ']') return;
      if (separator !== ',') throw new Error('fleet_recovery_json_invalid');
    }
  }
}
